import { Component, PLATFORM_ID, Renderer2, OnDestroy, ChangeDetectorRef, inject, effect, DOCUMENT, computed } from '@angular/core';

import { isPlatformBrowser, isPlatformServer, NgClass, ViewportScroller } from '@angular/common';
import {
  Router,
  NavigationEnd,
  RouterOutlet,
  NavigationStart,
  NavigationCancel,
  Scroll,
  ActivatedRouteSnapshot,
  RouterLink,
  NavigationError,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { FromDictionaryPipe, getDictionaryLoaded } from '@teamfoster/sdk/dictionary-ngrx';
import { Subject } from 'rxjs';

import * as navActions from './store/actions/nav.action';
import * as navSelectors from './store/selectors/nav.selector';
import { filter, map, pairwise } from 'rxjs/operators';
import { routerFade } from './animations';
import { ChangeDetectionStrategy } from '@angular/core';
import { CookieConfigService, CookieNoticeComponent, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { getActiveActionBar } from './action-bars/store';
import { FromMenuPipe, getMenuLoaded } from '@teamfoster/sdk/menu-ngrx';
import * as fromShop from './shop/store';
import { Customer } from './shop';
import { Line } from './shop/models/cart.model';
import { getActiveLanguage, getSwitchableLanguages } from '@teamfoster/sdk/language-ngrx';
import { environment } from 'src/environments/environment';
import { getRouterState } from './store';
import { getNewsLetterShowWindow } from './newsletters/store';
import { ChatStatus } from './core/models/chatStatus.model';
import { getAllSocialChannels } from './social-media/store';
import { Analyzify } from './utility/analyzify.utility';
import { MetaPixel } from './utility/meta-pixel.utility';
import { getActiveStorefront } from './storefronts';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { GridOverlayComponent } from './core/components/grid-overlay/grid-overlay.component';
import { CartComponent } from './cart/components/cart/cart.component';
import { TopbarComponent } from './core/components/topbar/topbar.component';
import { FooterComponent } from './core/components/footer/footer.component';
import { ActionBarComponent } from './action-bars/components/action-bar/action-bar.component';
import { LangSwitcherComponent } from './core/components/lang-switcher/lang-switcher.component';
import { IconComponent } from './core/components/icon/icon.component';
import { LoaderIndicatorComponent } from './core/components/loading-indicator/loading-indicator.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  animations: [routerFade],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GridOverlayComponent,
    CartComponent,
    RouterOutlet,
    TopbarComponent,
    FooterComponent,
    ActionBarComponent,
    LangSwitcherComponent,
    NgClass,
    FromMenuPipe,
    RouterLink,
    IconComponent,
    FromDictionaryPipe,
    CookieNoticeComponent,
    LoaderIndicatorComponent,
  ],
})
export class AppComponent implements OnDestroy {
  #viewportScroller = inject(ViewportScroller);
  private store = inject(Store);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private cookieConfig = inject(CookieConfigService);
  private cd = inject(ChangeDetectorRef);

  dictionaryLoaded$ = this.store.selectSignal(getDictionaryLoaded);
  menuLoaded$ = this.store.selectSignal(getMenuLoaded);
  menuOpen$ = this.store.selectSignal(navSelectors.getMenuOpen);
  searchOpen$ = this.store.selectSignal(navSelectors.getSearchOpen);
  filtersOpen$ = this.store.selectSignal(navSelectors.getFiltersOpen);
  menuTransparent$ = this.store.selectSignal(navSelectors.getNavTransparency);
  cartPaneOpen$ = this.store.selectSignal(navSelectors.getCartPaneOpen);
  theme$ = this.store.selectSignal(navSelectors.getTheme);
  actionBar$ = this.store.selectSignal(getActiveActionBar);
  cookiePrefs$ = this.store.selectSignal(getCookiePreferences);
  cart$ = this.store.selectSignal(fromShop.getCart);
  chatStatus$ = this.store.selectSignal(navSelectors.getChatStatus);
  activeStorefront$ = this.store.selectSignal(getActiveStorefront);
  socialChannels$ = this.store.selectSignal(getAllSocialChannels);

  private routerEvents$ = toSignal(
    this.router.events.pipe(
      filter(e => [NavigationStart, NavigationEnd, NavigationCancel, NavigationError, Scroll].some(b => e instanceof b))
    )
  );
  loading$ = computed(
    () =>
      !(
        this.routerEvents$() instanceof NavigationEnd ||
        this.routerEvents$() instanceof NavigationCancel ||
        this.routerEvents$() instanceof NavigationError ||
        this.routerEvents$() instanceof Scroll
      )
  );
  loaded$ = computed(() => this.dictionaryLoaded$() && this.menuLoaded$());

  showNewsletter$ = this.store.selectSignal(getNewsLetterShowWindow);
  customer$ = this.store.selectSignal(fromShop.getCustomer);
  subscription: any;

  languages$ = this.store.selectSignal(getSwitchableLanguages);
  activeLanguage$ = this.store.selectSignal(getActiveLanguage);
  domainName: string = '';

  routeState$ = this.store.selectSignal(getRouterState);

  testGridEnabled = false;
  routerAnimationState = '';

  private _unsubscribe$ = new Subject<void>();

  customerEffect = effect(async () => {
    var customer = this.customer$();

    if (customer) {
      await this.identifyCustomer(customer as Customer);
    }
  });

  cookieUpdate = effect(() => {
    const cookiePrefs = this.cookiePrefs$();
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (cookiePrefs?.marketing) {
      this.loadKlaviyo();
      this.loadFacebookPixel(environment.pixelCode);
    }
  });

  activeLanguageEffect = effect(() => {
    const activeLanguage = this.activeLanguage$();
    const langAttribute = activeLanguage?.split('-')[0];
    this.document.documentElement.setAttribute('lang', langAttribute || 'nl');
  });

  filtersOpenEffect = effect(() => {
    const filtersOpen = this.filtersOpen$();
    this.updateRoot(filtersOpen);
    this.setChatStatus(filtersOpen ? 'hidden' : 'default');
  });

  cartPaneOpenEffect = effect(() => {
    const cartPaneOpen = this.cartPaneOpen$();
    this.updateRoot(cartPaneOpen, 'cart-pane--open');
  });

  chatStatusEffect = effect(() => {
    const chatStatus = this.chatStatus$();
    this.setChatStatus(chatStatus);
  });

  routerEffect$ = effect(() => {
    const event = this.routerEvents$();
    const state = this.routeState$();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (event instanceof NavigationStart && event.navigationTrigger === 'imperative' && !state?.state?.queryParams) {
      this.closeSidebar();
      if (isPlatformBrowser(this.platformId)) {
        if ((<any>window).dataLayer) {
          this.clearDataLayer();
        }
      }
    }
    if (event instanceof NavigationEnd && this.cookiePrefs$()?.analytical) {
      if ((<any>window).gtag) {
        (<any>window).gtag('config', this.cookieConfig.analyticsCode, { page_path: event.urlAfterRedirects, anonymize_ip: true });
      }
      if ((<any>window).dataLayer) {
        MetaPixel.PageView();
        Analyzify.PageViewAsync(state?.state.url ? '' : 'homepage', this.customer$() as Customer);
      }
    }
    this.document.querySelector('body')?.classList.add('set--in');
    this.document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  });

  cookiePrefsEffect = effect(() => {});

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.loadChat();
    }
    this.#handleScrollOnNavigation();

    toObservable(this.cookiePrefs$)
      .pipe(
        takeUntilDestroyed(),
        pairwise(),
        filter(([a, b]) => a?.analytical !== b?.analytical && b!.analytical),
        map(([, b]) => b)
      )
      .subscribe(prefs => {
        if ((<any>window).gtag) {
          (<any>window).gtag('config', this.cookieConfig.analyticsCode, {
            page_path: this.routeState$().state.url,
            anonymize_ip: true,
          });
        }
        if ((<any>window).dataLayer && prefs?.analytical) {
          MetaPixel.PageView();
          Analyzify.PageViewAsync(this.routeState$().state.url ? '' : 'homepage', this.customer$() as Customer);
        }
      });
  }

  openNewsletterPopup() {
    if (!(window as any)['klaviyo']) {
      return;
    }
    (window as any)['klaviyo'].openForm('XvgKBU');
  }

  async identifyCustomer(customer: Customer) {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    if (!(window as any)['klaviyo']) {
      return;
    }
    await (window as any)['klaviyo'].identify({
      email: customer.email,
    });
  }

  loadFacebookPixel(facebookPixelCode: string) {
    if (facebookPixelCode && typeof (<any>window).fbq === 'undefined') {
      const $script = document.createElement('script');
      $script.id = 'Facebook';
      $script.async = true;
      $script.innerHTML = `!function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            }; if (!f._fbq) f._fbq = n;
            n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
        }(window,
            document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

        fbq.disablePushState = true; //not recommended, but can be done
        fbq('init', '${facebookPixelCode}');`;

      document.head.prepend($script);
    }
  }

  loadKlaviyo() {
    if ((<any>window).klaviyo === undefined) {
      const $script = document.createElement('script');
      $script.id = 'Klaviyo';
      $script.async = true;
      $script.type = 'text/javascript';
      $script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=TzwGaS';

      document.body.append($script);
    }
  }

  toggleMenu() {
    this.store.dispatch(navActions.ToggleMenu());
  }

  closeMenu() {
    this.store.dispatch(navActions.CloseMenu());
  }

  openMenu() {
    this.store.dispatch(navActions.OpenMenu());
  }

  closeSidebar() {
    this.store.dispatch(navActions.CloseCart());
  }

  closeOverlays() {
    this.closeMenu();
    this.store.dispatch(navActions.CloseSearch());
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData;
  }

  private updateRoot(overlayActive: boolean, cssClass: string = 'main-nav--active') {
    overlayActive ? this.renderer?.addClass(this.document.body, cssClass) : this.renderer?.removeClass(this.document.body, cssClass);
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  private loadChat() {
    const baseScript = document.createElement('script');
    baseScript.async = true;
    baseScript.src =
      'https://cdn.shopify.com/shopifycloud/shopify_chat/storefront/shopifyChatV1.js?api_env=production&c=%23dea294&i=chat_bubble&p=bottom_left&s=text&shop_id=XwvZsEGKfQHxDr6RrCyH98JnXPzdLNe4dW4esOdmDAw&t=no_text&v=1&vp=lowest&shop=kado-design-v3.myshopify.com';
    document.body.append(baseScript);
  }

  toggleCart() {
    this.store.dispatch(navActions.ToggleCart());
  }

  setChatStatus(status: ChatStatus) {
    if (isPlatformBrowser(this.platformId)) {
      const el = document.querySelector('#dummy-chat-button-iframe');
      if (el) {
        el.className = ''; // reset all classes
        el.classList.add(status);
      }
    }
  }

  getNewLine(lines: Line[]) {
    if (!lines || !lines.length) {
      return null;
    }
    return [...lines]?.reverse().slice(0, 1)[0];
  }

  clearDataLayer() {
    if ((<any>window).dataLayer !== undefined && (<any>window).google_tag_manager !== undefined) {
      (<any>window).dataLayer.length = 0;
      const gtmContainerReg = /GTM-/i;

      for (const gtmKey of Object.keys((<any>window).google_tag_manager)) {
        if (
          gtmContainerReg.test(gtmKey) &&
          (<any>window).google_tag_manager[gtmKey].dataLayer &&
          (<any>window).google_tag_manager[gtmKey].dataLayer.reset
        ) {
          (<any>window).google_tag_manager[gtmKey].dataLayer.reset();
        }
      }
    }
  }

  #handleScrollOnNavigation(): void {
    this.router.events
      .pipe(
        // import { Event } from '@angular/router'
        takeUntilDestroyed(),
        filter(e => e instanceof Scroll),
        pairwise()
      )
      .subscribe((e: Scroll[]) => {
        const previous = e[0];
        const current = e[1];

        if (current.position) {
          // Backward navigation
          this.#viewportScroller.scrollToPosition(current.position);
        } else if (current.anchor) {
          // Anchor navigation
          this.#viewportScroller.scrollToAnchor(current.anchor);
        } else if (previous.routerEvent instanceof NavigationEnd && current.routerEvent instanceof NavigationEnd) {
          // Check if routes match, or if it is only a query param change
          if (this.#getBaseRoute(previous.routerEvent.urlAfterRedirects) !== this.#getBaseRoute(current.routerEvent.urlAfterRedirects)) {
            // Routes don't match, this is actual forward navigation
            // Default behavior: scroll to top
            this.#viewportScroller.scrollToPosition([0, 0]);
          } else if (this.#getChildNode(this.router.routerState.snapshot.root).data['scrollToTop']) {
            // get route data
            this.#viewportScroller.scrollToPosition([0, 0]);
          }
        }
      });
  }

  #getBaseRoute(url: string): string {
    // return url without query params
    return url.split('?')[0];
  }
  #getChildNode(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    if (route.firstChild) {
      return this.#getChildNode(route.firstChild);
    }
    return route;
  }
}
